import { z } from "zod";

import { canonicalJson, sha256Hex, type JsonValue } from "./canonical.js";
import { CalcDecimal } from "./decimal.js";
import {
  createSignedDm2016AuthoritativeRecalculation,
  createSignedDm2016BundleAuthoritativeRecalculation,
  dm2016AuthoritativeRequestSchema,
  dm2016BundleAuthoritativeRequestSchema,
  type Dm2016AuthoritativePayload,
  type Dm2016BundleAuthoritativePayload,
} from "./dm2016-authoritative-recalculation.js";
import type { Dm2016OperationalReleaseResult } from "./dm2016-operational-release.js";
import type { Dm2016ReferenceRulesetIdentity } from "./dm2016-reference.js";
import {
  classifyEquoCompenso,
  equoCompensoScreeningInputSchema,
  resolveEquoCompensoEngine,
} from "./equo-compenso.js";
import { publicTechnicalExpenseMaximumPercentage } from "./public-technical.js";
import { calculatePublicQuoteFinancials, type PublicQuoteFinancialsResult } from "./quote-financials.js";

export const DM2016_CANONICAL_QUOTE_SCHEMA_VERSION = "3.0.0" as const;
export const DM2016_CANONICAL_QUOTE_ROUTE = "dm2016_authoritative_quote" as const;

const decimalSchema = z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/);
const identifierSchema = z.string().min(1).max(100).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
const additionalItemSchema = z.object({
  itemId: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/),
  label: z.string().trim().min(1).max(200),
  amount: z.string().regex(/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/).refine(
    (value) => new CalcDecimal(value).gt(0),
    "Additional item amount must be positive",
  ),
}).strict();

export const dm2016CanonicalQuoteRequestSchema = z.object({
  schemaVersion: z.literal(DM2016_CANONICAL_QUOTE_SCHEMA_VERSION),
  requestId: identifierSchema,
  route: z.literal(DM2016_CANONICAL_QUOTE_ROUTE),
  screening: equoCompensoScreeningInputSchema,
  calculation: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("single"), request: dm2016AuthoritativeRequestSchema }).strict(),
    z.object({ kind: z.literal("bundle"), request: dm2016BundleAuthoritativeRequestSchema }).strict(),
  ]),
  economicInput: z.object({
    worksValueForExpenseLimit: decimalSchema.refine(
      (value) => new CalcDecimal(value).gt(0),
      "Works value for expense limit must be positive",
    ),
    bimMandatory: z.boolean(),
    expensePercentage: decimalSchema,
    reductionPercentage: decimalSchema,
    socialSecurityPercentage: decimalSchema,
    vatPercentage: decimalSchema,
    additionalItems: z.array(additionalItemSchema).max(50),
  }).strict(),
}).strict();

export type Dm2016CanonicalQuoteRequest = z.infer<typeof dm2016CanonicalQuoteRequestSchema>;
type CanonicalCalculation = Dm2016AuthoritativePayload | Dm2016BundleAuthoritativePayload;

export interface Dm2016CanonicalQuotePayload {
  readonly schemaVersion: typeof DM2016_CANONICAL_QUOTE_SCHEMA_VERSION;
  readonly requestId: string;
  readonly route: typeof DM2016_CANONICAL_QUOTE_ROUTE;
  readonly status: "server_verified_operational_calculation";
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly professionalUseAllowed: true;
  readonly legalCertification: false;
  readonly normativeRouting: {
    readonly route: "public_contracts_annex_i13";
    readonly engine: "dm2016_annex_i13";
    readonly procedure: "article_108_2_b_best_value" | "article_50_1_b_direct_award";
    readonly screeningVersion: string;
    readonly reasons: readonly string[];
  };
  readonly calculation: CanonicalCalculation;
  readonly economics: PublicQuoteFinancialsResult;
  readonly versions: {
    readonly releaseId: string;
    readonly releaseVersion: string;
    readonly rulesetVersion: string;
    readonly engineVersion: string;
    readonly quoteSchemaVersion: typeof DM2016_CANONICAL_QUOTE_SCHEMA_VERSION;
    readonly interpretationMode: "printed_source_strict" | "professionearchitetto_v0_74";
  };
  readonly hashes: {
    readonly inputSha256: string;
    readonly calculationResultSha256: string;
    readonly resultSha256: string;
    readonly quoteSha256: string;
  };
}

export interface SignedDm2016CanonicalQuote {
  readonly payload: Dm2016CanonicalQuotePayload;
  readonly signature: { readonly algorithm: "HMAC-SHA256"; readonly keyId: string; readonly value: string };
}

const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const signedDm2016CanonicalQuoteSchema = z.object({
  payload: z.object({
    schemaVersion: z.literal(DM2016_CANONICAL_QUOTE_SCHEMA_VERSION),
    requestId: identifierSchema,
    route: z.literal(DM2016_CANONICAL_QUOTE_ROUTE),
    status: z.literal("server_verified_operational_calculation"),
    issuedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    professionalUseAllowed: z.literal(true),
    legalCertification: z.literal(false),
    normativeRouting: z.object({
      route: z.literal("public_contracts_annex_i13"),
      engine: z.literal("dm2016_annex_i13"),
      procedure: z.enum(["article_108_2_b_best_value", "article_50_1_b_direct_award"]),
    }).passthrough(),
    calculation: z.record(z.string(), z.unknown()),
    economics: z.record(z.string(), z.unknown()),
    versions: z.record(z.string(), z.unknown()),
    hashes: z.object({
      inputSha256: sha256Schema,
      calculationResultSha256: sha256Schema,
      resultSha256: sha256Schema,
      quoteSha256: sha256Schema,
    }).strict(),
  }).passthrough(),
  signature: z.object({
    algorithm: z.literal("HMAC-SHA256"),
    keyId: identifierSchema,
    value: sha256Schema,
  }).strict(),
}).strict();

export class Dm2016CanonicalQuoteError extends Error {
  constructor(
    readonly code: "INVALID_REQUEST" | "ROUTING_BLOCKED" | "SIGNING_CONFIGURATION_INVALID",
    message: string,
    readonly httpStatus: 400 | 409 | 503,
  ) {
    super(message);
    this.name = "Dm2016CanonicalQuoteError";
  }
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function hexToBytes(value: string): Uint8Array | null {
  if (!/^[a-f0-9]{64}$/.test(value)) return null;
  const bytes = new Uint8Array(32);
  for (let index = 0; index < value.length; index += 2) bytes[index / 2] = Number.parseInt(value.slice(index, index + 2), 16);
  return bytes;
}

async function signingKey(secret: string): Promise<CryptoKey> {
  const bytes = new TextEncoder().encode(secret);
  if (bytes.byteLength < 32) {
    throw new Dm2016CanonicalQuoteError("SIGNING_CONFIGURATION_INVALID", "Il segreto di firma server deve contenere almeno 32 byte.", 503);
  }
  return globalThis.crypto.subtle.importKey("raw", bytes, { name: "HMAC", hash: "SHA-256" }, false, ["sign", "verify"]);
}

export async function createSignedDm2016CanonicalQuote(input: {
  readonly request: unknown;
  readonly z1: unknown;
  readonly z2Matrix: unknown;
  readonly selectionRules: unknown;
  readonly identity: Dm2016ReferenceRulesetIdentity;
  readonly operationalRelease: Dm2016OperationalReleaseResult;
  readonly releaseBlockers: readonly string[];
  readonly signingSecret: string;
  readonly keyId: string;
  readonly issuedAt?: Date;
  readonly ttlSeconds?: number;
}): Promise<SignedDm2016CanonicalQuote> {
  const parsed = dm2016CanonicalQuoteRequestSchema.safeParse(input.request);
  if (!parsed.success) throw new Dm2016CanonicalQuoteError("INVALID_REQUEST", "Canonical quote request non valida.", 400);
  if (!identifierSchema.safeParse(input.keyId).success) throw new Dm2016CanonicalQuoteError("SIGNING_CONFIGURATION_INVALID", "Key ID non valido.", 503);
  const request = parsed.data;
  const screening = classifyEquoCompenso(request.screening);
  const routing = resolveEquoCompensoEngine(screening);
  const procedure = request.screening.publicAwardProcedure;
  if (routing.engine !== "dm2016_annex_i13" || !routing.calculationAllowed || (procedure !== "article_108_2_b_best_value" && procedure !== "article_50_1_b_direct_award")) {
    throw new Dm2016CanonicalQuoteError("ROUTING_BLOCKED", routing.reason, 409);
  }
  const issuedAt = input.issuedAt ?? new Date();
  const ttlSeconds = input.ttlSeconds ?? 600;
  if (!Number.isInteger(ttlSeconds) || ttlSeconds < 30 || ttlSeconds > 3600 || Number.isNaN(issuedAt.getTime())) {
    throw new Dm2016CanonicalQuoteError("SIGNING_CONFIGURATION_INVALID", "Durata o data firma non valida.", 503);
  }
  const common = {
    z1: input.z1,
    z2Matrix: input.z2Matrix,
    selectionRules: input.selectionRules,
    identity: input.identity,
    operationalRelease: input.operationalRelease,
    releaseBlockers: input.releaseBlockers,
    signingSecret: input.signingSecret,
    keyId: input.keyId,
    issuedAt,
    ttlSeconds,
  } as const;
  const signedCalculation = request.calculation.kind === "single"
    ? await createSignedDm2016AuthoritativeRecalculation({ ...common, request: request.calculation.request })
    : await createSignedDm2016BundleAuthoritativeRecalculation({ ...common, request: request.calculation.request });
  const calculation = signedCalculation.payload;
  const worksValueForExpenseLimit = new CalcDecimal(
    request.economicInput.worksValueForExpenseLimit,
  ).toString();
  const componentValues = "traces" in calculation
    ? calculation.normalizedInput.services
        .filter((service) => service.serviceCode !== "QcI.07" && service.serviceCode !== "QcI.08")
        .map((service) => service.valueV)
    : calculation.normalizedInput.serviceCode === "QcI.07"
        || calculation.normalizedInput.serviceCode === "QcI.08"
      ? []
      : [calculation.normalizedInput.valueV];
  if (componentValues.some((value) => new CalcDecimal(value).gt(worksValueForExpenseLimit))) {
    throw new Dm2016CanonicalQuoteError(
      "INVALID_REQUEST",
      "L'importo complessivo dell'opera non può essere inferiore al valore V di una categoria componente (escluse le varianti QcI.07/QcI.08).",
      400,
    );
  }
  const economics = calculatePublicQuoteFinancials({
    verifiedCompensation: calculation.calculation.compensationRounded,
    worksValueForExpenseLimit,
    procedure,
    bimMandatory: request.economicInput.bimMandatory,
    expensePercentage: request.economicInput.expensePercentage,
    expenseMaximumPercentage: publicTechnicalExpenseMaximumPercentage(worksValueForExpenseLimit),
    reductionPercentage: request.economicInput.reductionPercentage,
    socialSecurityPercentage: request.economicInput.socialSecurityPercentage,
    vatPercentage: request.economicInput.vatPercentage,
    additionalItems: request.economicInput.additionalItems,
  });
  const release = input.operationalRelease.manifest;
  if (release === null || !input.operationalRelease.ready) throw new Dm2016CanonicalQuoteError("SIGNING_CONFIGURATION_INVALID", "Gate operativo chiuso.", 503);
  const calculationRequest = request.calculation.request;
  const calculationResultSha256 = calculation.calculation.resultSha256;
  const resultSha256 = await sha256Hex(canonicalJson({ calculationResultSha256, economics } as unknown as JsonValue));
  const payloadWithoutQuoteHash = {
    schemaVersion: DM2016_CANONICAL_QUOTE_SCHEMA_VERSION,
    requestId: request.requestId,
    route: DM2016_CANONICAL_QUOTE_ROUTE,
    status: "server_verified_operational_calculation" as const,
    issuedAt: issuedAt.toISOString(),
    expiresAt: new Date(issuedAt.getTime() + ttlSeconds * 1_000).toISOString(),
    professionalUseAllowed: true as const,
    legalCertification: false as const,
    normativeRouting: {
      route: "public_contracts_annex_i13" as const,
      engine: "dm2016_annex_i13" as const,
      procedure,
      screeningVersion: screening.classificationVersion,
      reasons: screening.reasons,
    },
    calculation,
    economics,
    versions: {
      releaseId: release.release_id,
      releaseVersion: release.release_version,
      rulesetVersion: calculation.calculation.rulesetVersion,
      engineVersion: calculation.calculation.engineVersion,
      quoteSchemaVersion: DM2016_CANONICAL_QUOTE_SCHEMA_VERSION,
      interpretationMode: calculationRequest.interpretationMode,
    },
    hashes: { inputSha256: calculation.calculation.inputSha256, calculationResultSha256, resultSha256 },
  };
  const quoteSha256 = await sha256Hex(canonicalJson(payloadWithoutQuoteHash as unknown as JsonValue));
  const payload: Dm2016CanonicalQuotePayload = { ...payloadWithoutQuoteHash, hashes: { ...payloadWithoutQuoteHash.hashes, quoteSha256 } };
  const key = await signingKey(input.signingSecret);
  const signature = new Uint8Array(await globalThis.crypto.subtle.sign("HMAC", key, new TextEncoder().encode(canonicalJson(payload as unknown as JsonValue))));
  return freezeDeep({ payload, signature: { algorithm: "HMAC-SHA256" as const, keyId: input.keyId, value: bytesToHex(signature) } });
}

export async function verifySignedDm2016CanonicalQuote(input: {
  readonly document: SignedDm2016CanonicalQuote;
  readonly signingSecret: string;
  readonly expectedKeyId: string;
  readonly at?: Date;
}): Promise<boolean> {
  const parsedDocument = signedDm2016CanonicalQuoteSchema.safeParse(input.document);
  if (!parsedDocument.success) return false;
  const document = parsedDocument.data as unknown as SignedDm2016CanonicalQuote;
  const signature = hexToBytes(document.signature.value);
  if (signature === null || document.signature.algorithm !== "HMAC-SHA256" || document.signature.keyId !== input.expectedKeyId) return false;
  const at = input.at ?? new Date();
  const issuedAt = Date.parse(document.payload.issuedAt);
  const expiresAt = Date.parse(document.payload.expiresAt);
  if (Number.isNaN(at.getTime()) || Number.isNaN(issuedAt) || Number.isNaN(expiresAt) || at.getTime() < issuedAt || at.getTime() > expiresAt) return false;
  if (!await validateDm2016CanonicalQuoteIntegrity(document)) return false;
  try {
    const key = await signingKey(input.signingSecret);
    const buffer = new ArrayBuffer(signature.byteLength);
    new Uint8Array(buffer).set(signature);
    return globalThis.crypto.subtle.verify("HMAC", key, buffer, new TextEncoder().encode(canonicalJson(document.payload as unknown as JsonValue)));
  } catch {
    return false;
  }
}

export async function validateDm2016CanonicalQuoteIntegrity(
  input: unknown,
): Promise<boolean> {
  const parsed = signedDm2016CanonicalQuoteSchema.safeParse(input);
  if (!parsed.success) return false;
  const document = parsed.data as unknown as SignedDm2016CanonicalQuote;
  const expectedResultHash = await sha256Hex(canonicalJson({
    calculationResultSha256: document.payload.hashes.calculationResultSha256,
    economics: document.payload.economics,
  } as unknown as JsonValue));
  if (expectedResultHash !== document.payload.hashes.resultSha256) return false;
  const { quoteSha256: _quoteSha256, ...hashesWithoutQuote } = document.payload.hashes;
  const expectedQuoteHash = await sha256Hex(canonicalJson({
    ...document.payload,
    hashes: hashesWithoutQuote,
  } as unknown as JsonValue));
  return expectedQuoteHash === document.payload.hashes.quoteSha256;
}
